/**
 * @file
 * Main trips table
 *
 * @format
 * @flow strict-local
 */

import React, { useCallback, useState } from 'react';
import styled from '@emotion/native';
import { DateTime } from 'luxon';
import _ from 'lodash';
import { useAsyncCallback } from 'react-async-hook';
import { ActivityIndicator, Badge } from 'react-native-paper';

import app, { FirestoreError } from '@appFirebase';
import * as Manager from '@appUtils/manager';
import DataTable, { Row, Cell, PlainCell } from '@appComponents/DataTable';
import {
  PilotState,
  TripState,
  Trip,
  OwnerState,
} from '@appUtils/tripConverter';
import Button, { LinkButton } from '@appComponents/Button';
import { Menu, MenuItem, useMenuState } from '@appComponents/Menu';
import Text from '@appComponents/Text';
import { Box, Circle, ScreenLoader } from '@appComponents/ScreenLayout';
import Checkbox from '@appComponents/Checkbox';
import { SquawkLegend } from './SquawkLegend';
import { formatNumber } from '@appUtils/numbers';
import {
  getSquawkColorByPriority,
  SquawkPriority,
  SquawkStatus,
} from '@appUtils/squawks';
import { useTheme } from '@appComponents/theme';
import { getTripStateName } from '@appUtils/trip';

type TripTableProps = {
  loading?: boolean,
  data?: Array<Trip>,
  error?: FirestoreError,
  initialSort?: { key: string, dir: 'asc' | 'desc' },
  onSortChange?: ({ key: string, dir: 'asc' | 'desc' }) => void,
  bulkActions?: boolean,
  statusCol?: boolean,
  highlightRequests?: boolean,
  aircraft?: Array<Aircraft>,
  unreadMessages?: Array<Object>,
  accounting?: boolean,
};

const TripsTable = ({
  loading,
  data,
  error,
  initialSort,
  onSortChange,
  bulkActions,
  statusCol,
  highlightRequests,
  unreadMessages,
  aircraft,
  accounting,
}) => {
  let emptyMessage = <Text>No Trips Available</Text>;

  if (loading && _.isEmpty(data)) {
    emptyMessage = <Text>Loading...</Text>;
  }
  if (error) {
    emptyMessage = <Text>Failed to load Trip. Try again later</Text>;
  }

  const [checked, setChecked] = useState([]);

  const RowRenderer = useRowRenderer({
    highlightRequests: highlightRequests,
    checked,
  });

  return (
    <Box flex={1}>
      {loading && <ScreenLoader />}
      {!accounting && (
        <Legend
          selectionMode={bulkActions}
          checked={checked}
          loading={loading}
          setChecked={setChecked}
          all={data}
        />
      )}
      <TableLayout
        data={data}
        RowComponent={RowRenderer}
        keyExtractor={keyExtractor}
        initialSort={initialSort}
        onSortChange={onSortChange}
        footerMessage={loading && _.size(data) > 0 && <ActivityIndicator />}
        headerStyle={headerStyle}
        emptyMessage={
          <Box ai="center" mt={1}>
            {emptyMessage}
          </Box>
        }>
        {bulkActions && (
          <CheckboxCell
            name="check"
            flex={3}
            setChecked={setChecked}
            alreadyChecked={checked}
          />
        )}
        {statusCol && <StateCell title="State" flex={12} sortable />}
        <IdCell
          title="ID"
          flex={7}
          sortable
          unreadMessages={unreadMessages}
          accounting={accounting}
        />
        {accounting && <StateCell title="State" flex={12} sortable />}
        <PlainCell title="Name" path="customName" flex={15} sortable />
        <TailCell title="Tail #" allAircraft={aircraft} flex={5} sortable />
        <LegsCell title="Legs" flex={8} sortable />
        <DepartureCell title="Departure" col="departure" flex={13} sortable />
        {!accounting && <PilotCell title="Pilot Status" flex={8} sortable />}
        {!accounting && <OwnerCell title="Client Status" flex={8} sortable />}
        {!accounting && (
          <ActionsCell
            name="actions"
            numeric
            flex={3}
            icon="more-dots"
            setChecked={setChecked}
          />
        )}
        {accounting && (
          <DueCell title="Due from Client" path="ownerDue" flex={8} sortable />
        )}
        {accounting && (
          <DueCell title="Due to Pilots" path="pilotDue" flex={8} sortable />
        )}
        {accounting && (
          <DueCell title="Net Due" path="netDue" flex={6} sortable />
        )}
      </TableLayout>
    </Box>
  );
};

const headerStyle = { paddingHorizontal: 8 };

const useRowRenderer = ({ highlightRequests = true, checked }) => {
  const theme = useTheme();
  return useCallback(
    ({ item, children }) => {
      let bg;
      if (highlightRequests && item.state === TripState.OWNER_REQUEST) {
        bg = theme.colors.selectedRow;
      }

      if (_.includes(checked, item.path)) {
        bg = theme.colors.row;
      }

      return <RowLayout bg={bg}>{children}</RowLayout>;
    },
    [checked, highlightRequests, theme.colors],
  );
};

const keyExtractor = trip => trip.path;

const Legend = ({ checked, selectionMode, all, loading, setChecked }) => {
  const { open, action, ...bulkActions } = useStateActions(checked, setChecked);

  const checkbox = selectionMode ? (
    <Checkbox
      label="Select All"
      disabled={loading || _.isEmpty(all)}
      checked={_.size(all) > 0 && _.size(checked) === _.size(all)}
      onChange={() => setChecked(current => (_.isEmpty(current) ? all : []))}
    />
  ) : null;

  return (
    <SquawkLegend
      checked={checked}
      checkbox={checkbox}
      bulkActions={bulkActions}
      action={action}
      actions={
        // Rendered bulk actions are the union of all actions for the checked trips, excluding delete as needed
        <BulkActions trips={checked} action={action} {...bulkActions} />
      }
      open={open}
    />
  );
};

const CheckboxCell = ({ item: trip, flex, alreadyChecked, setChecked }) => {
  const checked = _.some(alreadyChecked, t => trip.identifier === t.identifier);

  return (
    <CheckboxWrapper flex={flex}>
      <Checkbox
        label=""
        checked={checked}
        onChange={() => {
          if (checked) {
            setChecked(
              alreadyChecked.filter(t => t.identifier !== trip.identifier),
            );
          } else {
            setChecked([...alreadyChecked, trip]);
          }
        }}
      />
    </CheckboxWrapper>
  );
};

const CheckboxWrapper = styled(Cell)`
  padding-horizontal: 0;
  margin-right: 0;
`;

const StateCell = React.memo(
  ({ item: trip, flex }) => (
    <Cell flex={flex}>
      <StatusBadge>
        <Text size="smallest">{trip.state?.toUpperCase()}</Text>
      </StatusBadge>
    </Cell>
  ),
  (a, b) => a.item.state === b.item.state,
);

const DueCell = React.memo(
  ({ item: trip, title, path, flex }) => (
    <Cell flex={flex} title={title} path={path}>
      <Text>${formatNumber({ value: trip[path].toFixed(2) })}</Text>
    </Cell>
  ),
  (a, b) => a.item.state === b.item.state,
);

const getTripTitle = trip => {
  return [getTripStateName(trip?.state, ''), trip?.identifier, trip?.customName]
    .filter(Boolean)
    .join(' - ');
};

const IdCell = React.memo(
  ({ item: trip, unreadMessages, accounting, flex, ...cellProps }) => {
    const labelStyle = { marginHorizontal: 0 };
    let tripScreen = 'Trip';
    let tripTitle = getTripTitle(trip);
    const tripUnreadMessages =
      _.find(unreadMessages, conversation => {
        return conversation?.tripId === trip.id;
      })?.managementUnreadMessages ?? 0;

    return (
      <FloatingBadgeWrap flex={flex}>
        <FloatingBadge size={15} unreadMessages={tripUnreadMessages}>
          {tripUnreadMessages}
        </FloatingBadge>
        <LinkButton
          toScreen={tripScreen}
          params={{
            documentPath: trip.path,
            title: tripTitle,
            tab: accounting ? 'EXPENSES' : undefined,
          }}
          mode="text"
          labelStyle={labelStyle}>
          {trip.identifier}
        </LinkButton>
      </FloatingBadgeWrap>
    );
  },
  (a, b) =>
    a.item.identifier === b.item.identifier &&
    _.isEqual(a.unreadMessages, b.unreadMessages),
);

const TailCell = React.memo(
  ({ item: trip, allAircraft, ...cellProps }) => {
    const tail = trip.aircraft?.tailNumber;
    const currentAircraft = _.find(
      allAircraft,
      aircraft => aircraft.tailNumber === tail,
    );
    const squawkPriority =
      currentAircraft?.squawkPriority ??
      SquawkPriority[SquawkStatus.NO_SQUAWKS];
    const squawkColor = getSquawkColorByPriority(squawkPriority);

    return (
      <Cell {...cellProps}>
        {tail && <Circle color={squawkColor} size={10} />} {tail || '-'}
      </Cell>
    );
  },
  (a, b) => _.isEqualWith(a, b, 'item.aircraft.tailNumber'),
);

const LegsCell = React.memo(
  ({ item: trip, ...cellProps }) => {
    const legs = trip.legs ?? [];
    const from = _.head(legs)?.from
      ? _.head(legs).from.length > 4
        ? `${_.head(legs).from.substring(0, 3)}...`
        : _.head(legs).from
      : '?';
    const to = _.last(legs)?.to
      ? _.last(legs).to.length > 4
        ? `${_.last(legs).to.substring(0, 3)}...`
        : _.last(legs).to
      : '?';

    return (
      <Cell {...cellProps}>
        {from} > {to}
      </Cell>
    );
  },
  (a, b) => _.isEqualWith(a, b, 'item.legs'),
);

const DepartureCell = React.memo(
  ({ item: trip, ...cellProps }) => {
    const departureDate = _.head(trip.legs)?.departureDate;
    if (!departureDate) {
      return <Cell {...cellProps}>-</Cell>;
    }

    const date = departureDate.toLocaleString(DateTime.DATE_SHORT);
    const time = departureDate.toLocaleString(DateTime.TIME_WITH_SHORT_OFFSET);
    const secondsIndex = time.lastIndexOf(':');

    return (
      <Cell {...cellProps}>
        <Text tw="wrap">
          {[
            date,
            time.slice(0, secondsIndex).concat(time.slice(secondsIndex + 3)),
          ].join(' @ ')}
        </Text>
      </Cell>
    );
  },
  (a, b) => _.isEqualWith(a, b, 'item.legs.0.departureDate'),
);

const PilotCell = React.memo(
  ({ item: trip, ...cellProps }) => {
    if (_.isEmpty(trip.pilots)) {
      return <Cell {...cellProps}>-</Cell>;
    }

    const content = trip.pilots.map((p, i) => {
      let state = p.state || PilotState.UNSENT;
      state = state.replace('Manager', 'Mgr.').replace('Requested', 'Req.');
      return (
        <>
          ({i === 0 ? 'P' : 'S'}IC) {state}
          <br />
        </>
      );
    });

    return <Cell {...cellProps}>{content}</Cell>;
  },
  (a, b) => _.isEqualWith(a, b, 'item.pilots'),
);

const OwnerCell = React.memo(
  ({ item: trip, ...cellProps }) => {
    if (_.isEmpty(trip.owner.state)) {
      return <Cell {...cellProps}>-</Cell>;
    }

    const content = trip.owner.state
      .replace('Manager', 'Mgr.')
      .replace('Requested', 'Req.');

    return <Cell {...cellProps}>{content}</Cell>;
  },
  (a, b) => _.isEqualWith(a, b, 'item.owner'),
);

const ActionsCell = React.memo(
  ({ item: trip, checked, label, icon, setChecked, ...cellProps }) => {
    const { action, open, ...menu } = useStateActions([trip], setChecked);

    return (
      <Cell ref={menu.anchorEl} {...cellProps}>
        <Button
          label={label}
          loading={action.loading}
          mode="text"
          icon={icon}
          onPress={open}
        />
        <Actions trip={trip} action={action} {...menu} />
      </Cell>
    );
  },
  (a, b) => a.item.state === b.item.state,
);

const Actions = ({ anchorEl, isOpen, close, action, trip }) => (
  <Menu anchor={anchorEl} visible={isOpen} onDismiss={close}>
    {getActionsForState(trip).map(entry => (
      <ActionItem key={entry.title} action={action} {...entry} />
    ))}
  </Menu>
);

const BulkActions = ({ anchorEl, isOpen, close, action, trips }) => {
  if (trips.length === 1) {
    return (
      <Menu anchor={anchorEl} visible={isOpen} onDismiss={close}>
        {getActionsForState(trips[0]).map(entry => (
          <ActionItem key={entry.title} action={action} {...entry} />
        ))}
      </Menu>
    );
  }

  const addedStates = new Set();
  let excludeDelete = false;
  const bulkActions = [];
  trips.map(t => {
    if (!excludeDelete && !t.archived && NonDeleteStates.includes(t.state)) {
      excludeDelete = true;
    }
    if (!addedStates.has(t.state)) {
      addedStates.add(t.state);
      getActionsForState(t).map(a => {
        bulkActions.push(a);
      });
    }
  });
  const uniqueActions = _.uniqBy(bulkActions, a => a.nextState);

  if (excludeDelete) {
    const deleteIndex = _.findIndex(
      uniqueActions,
      a => a.nextState === 'Delete',
    );
    if (deleteIndex !== -1) {
      uniqueActions.splice(deleteIndex, 1);
    }
  }

  return (
    <Menu anchor={anchorEl} visible={isOpen} onDismiss={close}>
      {uniqueActions.map(entry => (
        <ActionItem key={entry.title} action={action} {...entry} />
      ))}
    </Menu>
  );
};

const NonDeleteStates = [
  TripState.OWNER_REQUEST,
  TripState.PILOT_REQUEST,
  TripState.DRAFT,
  TripState.UPCOMING,
  TripState.ACTIVE,
  TripState.ENDED,
];

const getActionsForState = (trip: Trip) => {
  if (trip.archived) {
    return [
      { nextState: 'Restore', title: 'Restore' },
      { nextState: 'Delete', title: 'Delete' },
    ];
  }

  const map = new Map([
    [
      TripState.OWNER_REQUEST,
      [
        { nextState: TripState.DRAFT, title: 'Acknowledge' },
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: TripState.CANCELLED, title: 'Reject' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.PILOT_REQUEST,
      [
        { nextState: TripState.DRAFT, title: 'Acknowledge' },
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: TripState.CANCELLED, title: 'Reject' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.DRAFT,
      [
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: TripState.CANCELLED, title: 'Cancel' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.UPCOMING,
      [
        { nextState: TripState.DRAFT, title: 'Move to Draft' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: TripState.CANCELLED, title: 'Cancel' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.ACTIVE,
      [
        { nextState: TripState.DRAFT, title: 'Move to Draft' },
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: TripState.CANCELLED, title: 'Cancel' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.ENDED,
      [
        { nextState: TripState.DRAFT, title: 'Move to Draft' },
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.CANCELLED, title: 'Cancel' },
        { nextState: 'Archive', title: 'Archive' },
      ],
    ],
    [
      TripState.CANCELLED,
      [
        { nextState: TripState.DRAFT, title: 'Move to Draft' },
        { nextState: TripState.UPCOMING, title: 'Move to Upcoming' },
        { nextState: TripState.ACTIVE, title: 'Move to Active' },
        { nextState: TripState.ENDED, title: 'Move to Ended' },
        { nextState: 'Archive', title: 'Archive' },
        { nextState: 'Delete', title: 'Delete' },
      ],
    ],
  ]);

  return map.get(trip.state) || [];
};

const useStateActions = (trips: Trip[], setChecked) => {
  const { close, ...menu } = useMenuState();
  const action = useAsyncCallback(async nextState => {
    const batch = app.firestore().batch();
    _.uniq<Trip>(trips).map(trip => {
      const doc = app.firestore().doc(trip.path);
      let updates = Manager.getPayload({ state: nextState }, trip);

      // Handle special case actions then regular state transitions
      if (nextState === 'Delete') {
        batch.delete(doc);
        setChecked([]);
        return;
      } else if (nextState === 'Archive') {
        updates = Manager.getPayload({ archived: true }, trip);
      } else if (nextState === 'Restore') {
        updates = Manager.getPayload({ archived: false }, trip);
      } else if (nextState === TripState.CANCELLED) {
        if (trip.owner) {
          updates.owner = {
            ...trip.owner,
            state: OwnerState.MANAGER_CANCELED,
          };
        }

        updates.pilots = _.map(trip.pilots, pilot => ({
          ...pilot,
          state: PilotState.MANAGER_CANCELED,
        }));
      } else if (nextState === TripState.ENDED) {
        const location = _.last(trip.legs)?.to;
        if (trip.aircraft?.path && location) {
          const aircraftDoc = app.firestore().doc(trip.aircraft.path);
          batch.update(aircraftDoc, { location });
        }
      } else if (nextState === TripState.DRAFT) {
        updates.owner = {
          ...trip.owner,
          state: OwnerState.MANAGER_ACKNOWLEDGED,
        };
      } else if (nextState === TripState.UPCOMING) {
        updates.owner = {
          ...trip.owner,
          state: OwnerState.MANAGER_UPDATED,
        };

        updates.pilots = _.map(trip.pilots, pilot => ({
          ...pilot,
          state: PilotState.MANAGER_UPDATED,
        }));
      }

      batch.update(doc, updates);
      setChecked([]);
    });

    close();
    return batch.commit();
  });

  return { action, close, ...menu };
};

const ActionItem = ({ title, action, nextState }) => {
  return (
    <MenuItem
      title={title}
      onPress={() => action.execute(nextState)}
      disabled={action.loading}
    />
  );
};

const TableLayout: DataTable = styled(DataTable)(({ theme }) => ({
  flex: 1,
  marginBottom: theme.layout.space(0.5),
}));

const RowLayout: Row = styled(Row)(({ theme, bg = 'background' }) => ({
  minHeight: 56,
  backgroundColor: _.get(theme.colors, bg, bg),
}));

const StatusBadge = styled.View(({ theme }) => ({
  borderRadius: 100,
  paddingVertical: theme.layout.space(0.375),
  paddingHorizontal: theme.layout.space(1),
  borderWidth: 1,
  borderColor: theme.colors.text,
}));

const FloatingBadgeWrap = styled.View(({ flex, builder }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  left: '-1%',
  flex: flex,
}));

const FloatingBadge = styled(Badge)(({ unreadMessages }) => ({
  alignSelf: 'center',
  marginRight: '-9%',
  marginTop: '-10%',
  visibility: unreadMessages > 0 ? 'visible' : 'hidden',
  zIndex: 10,
}));

export default TripsTable;
